import { z } from "zod";

export const identitySchema = z.object({
  subject: z.string(),
  email: z.email(),
  name: z.string(),
  admin: z.boolean(),
  expires: z.iso.datetime(),
});
export const serverSchema = z.object({
  id: z
    .string()
    .regex(
      /^[a-z][a-z0-9-]{0,62}$/,
      "Start with a lowercase letter; use lowercase letters, numbers and hyphens, up to 63 characters.",
    ),
  alias: z
    .string()
    .regex(
      /^[a-z][a-z0-9-]{0,62}$/,
      "Start with a lowercase letter; use lowercase letters, numbers and hyphens, up to 63 characters.",
    ),
  address: z.ipv4({ error: "Enter a valid NetBird IPv4 address, for example 100.105.169.45." }),
  port: z
    .number()
    .int("Enter a whole-number SSH port.")
    .min(1, "Use a port from 1 to 65535.")
    .max(65535, "Use a port from 1 to 65535."),
  login: z
    .string()
    .regex(
      /^[a-z_][a-z0-9_-]{0,31}$/,
      "Use a lowercase Linux account with letters, numbers, underscores or hyphens, up to 32 characters.",
    )
    .refine((v) => v !== "root", "Use a non-root Linux account"),
  peer_id: z.string().min(1, "Enter the peer ID shown for this server in NetBird."),
  provision_user: z
    .string()
    .min(1, "Enter the existing account used to configure SSH authorization."),
  host_key: z.string().regex(/^(ssh-|ecdsa-|sk-)/, "Paste an OpenSSH public host key"),
  enabled: z.boolean(),
});
export const ruleSchema = z.object({
  id: z
    .string()
    .regex(
      /^[a-z][a-z0-9-]{0,62}$/,
      "Start with a lowercase letter; use lowercase letters, numbers and hyphens, up to 63 characters.",
    ),
  server_id: z.string().min(1, "Choose the server account this group may access."),
  group_id: z.string().min(1, "Choose a designated NetBird SSH- group."),
  group_name: z.string().startsWith("SSH-"),
  enabled: z.boolean(),
});
export const stateSchema = z.object({
  servers: z.array(serverSchema),
  rules: z.array(ruleSchema),
  deployments: z.array(
    z.object({
      server_id: z.string(),
      desired: z.array(z.string()),
      applied: z.array(z.string()),
      status: z.string(),
      error: z.string(),
      updated_at: z.iso.datetime(),
    }),
  ),
  events: z.array(
    z.object({
      id: z.number(),
      actor: z.string(),
      action: z.string(),
      target: z.string(),
      detail: z.string(),
      at: z.iso.datetime(),
    }),
  ),
  last_sync: z.iso.datetime().nullable(),
  sync_error: z.string(),
});
export const netbirdPeerSchema = z.object({
  id: z.string().min(1),
  ip: z.string(),
  user_id: z.string(),
  name: z.string().optional().default(""),
  hostname: z.string().optional().default(""),
  dns_label: z.string().optional().default(""),
  os: z.string().optional().default(""),
  connected: z.boolean().nullable().optional(),
});
export type NetBirdPeer = z.infer<typeof netbirdPeerSchema>;
export const inventorySchema = z.object({
  groups: z.array(z.object({ id: z.string(), name: z.string() })),
  peers: z.array(netbirdPeerSchema),
});
export const peerInventorySchema = inventorySchema.pick({ peers: true });
export const aliasesSchema = z.array(
  z.object({
    alias: z.string(),
    address: z.string(),
    port: z.number(),
    login: z.string(),
    host_key: z.string(),
  }),
);
export type Server = z.infer<typeof serverSchema>;
export type Rule = z.infer<typeof ruleSchema>;
export type AccessState = z.infer<typeof stateSchema>;

export const serverFormSchema = serverSchema.omit({ enabled: true }).extend({
  port: z
    .string()
    .regex(/^\d+$/, "Enter a whole-number SSH port from 1 to 65535.")
    .refine((value) => Number(value) >= 1 && Number(value) <= 65535, "Use a port from 1 to 65535"),
});

export const consentSchema = z.object({ code: z.string(), email: z.email(), csrf: z.string() });
export const approvalSchema = z.object({ approved: z.boolean() });

export const apiErrorSchema = z.object({ error: z.string() });

export const recordingSchema = z.object({
  id: z.string().regex(/^[a-f0-9]{32}$/),
  connection_id: z.string(),
  lease_id: z.string(),
  email: z.email(),
  server_id: z.string(),
  alias: z.string(),
  login: z.string(),
  kind: z.string(),
  status: z.enum(["recording", "complete", "interrupted", "failed"]),
  created_at: z.iso.datetime(),
  expires_at: z.iso.datetime(),
  ended_at: z.iso.datetime().nullable(),
  bytes: z.number().int().nonnegative(),
  frames: z.number().int().nonnegative(),
});
export const historyPageSchema = z.object({ next_cursor: z.string(), previous_cursor: z.string() });
export const eventSchema = z.object({
  id: z.number().int(),
  actor: z.string(),
  action: z.string(),
  target: z.string(),
  detail: z.string(),
  at: z.iso.datetime(),
});
export const eventsSchema = z.object({
  items: z.array(eventSchema),
  actions: z.array(z.string()),
  page: historyPageSchema,
});
export const historySearchSchema = z.object({
  q: z.string().max(200).optional().catch(undefined),
  cursor: z.string().max(2048).optional().catch(undefined),
  status: z
    .enum(["all", "complete", "recording", "interrupted", "failed"])
    .optional()
    .catch(undefined),
  action: z.string().max(120).optional().catch(undefined),
});
export type HistorySearch = z.infer<typeof historySearchSchema>;
export const recordingsSchema = z.object({
  page: historyPageSchema.default({ next_cursor: "", previous_cursor: "" }),
  items: z.array(recordingSchema),
  storage: z.object({
    healthy: z.boolean(),
    used_bytes: z.number(),
    limit_bytes: z.number(),
    retention_days: z.number(),
  }),
});
export const recordingPreviewSchema = z.object({
  recording: recordingSchema,
  frames: z.array(
    z.object({ at: z.number().nonnegative(), kind: z.string(), data: z.string().default("") }),
  ),
  preview_limited: z.boolean(),
});
export type Recording = z.infer<typeof recordingSchema>;
export type RecordingPreview = z.infer<typeof recordingPreviewSchema>;
